import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { loadHarborDuels, registerHarborDuel } from '../api/harborDuelsApi';
import { loadShipyard } from '../api/shipyardApi';
import { useOptionalAppSession } from '../hooks/AppSessionContext';
import { useGameAudio } from '../game/audio/GameAudioContext';
import battleSoundUrl from '../assets/sounds/harbor-battle.mp3';
import cannonFireSoundUrl from '../assets/sounds/cannon-fire.mp3';

const FALLBACK_EVENTS = [
  { slug: 'hafenliga-der-werften', title: 'Hafenliga der Werften', badge: 'Anmeldung offen', rhythm: 'Wöchentlich', fights: '3 automatische Duelle', reward_label: 'Goldtruhe, Rangpunkte, seltene Werft-Ressource', can_register: false },
  { slug: 'kaperduell', title: 'Kaperduell', badge: 'Schnelles Match', rhythm: 'Täglich', fights: '1 Duell gegen ähnliche Schiffsstärke', reward_label: 'Bronze, Silberchance, kleine Beutetruhe', can_register: false },
  { slug: 'schatzflotten-regatta', title: 'Schatzflotten-Regatta', badge: 'Hafen-Event', rhythm: 'Wochenende', fights: 'Mehrere Schiffe jagen dieselbe NPC-Flotte', reward_label: 'Event-Beute, Kosmetik, Kartenfragmente', can_register: false },
];

const FALLBACK_PREP = [
  { slug: 'rope', label: 'Extra-Takelage', cost: 6, stat: '+8 Tempo', owned: 0, selected: true },
  { slug: 'nail_crate', label: 'Deck-Verstärkung', cost: 8, stat: '+7 Angriff', owned: 0, selected: true },
  { slug: 'resin', label: 'Harz-Versiegelung', cost: 1, stat: '+6 Rumpf', owned: 0, selected: false },
  { slug: 'sailcloth', label: 'Kampfflagge', cost: 1, stat: '+5 Initiative', owned: 0, selected: false },
  { slug: 'iron_fittings', label: 'Eisenbeschlag', cost: 1, stat: '+10 Abwehr', owned: 0, selected: false },
];

const FALLBACK_OPPONENTS = [
  { name: 'Küstenklopper', ship: 'Schwere Sammler-Brigg', power: 82, odds: 'Ausgeglichen' },
  { name: 'Pfandadmiral', ship: 'Schneller Kronkorken-Kutter', power: 74, odds: 'Gute Chance' },
  { name: 'Leergut-Legende', ship: 'Goldener Hafenjäger', power: 91, odds: 'Riskant' },
];

const BATTLE_REPLAY_MS = 5200;

const BATTLE_PHASE_LABELS = {
  idle: '',
  preparing: 'Der Hafenmeister gibt das Signal...',
  broadside: 'Breitseiten! Kanonenkugeln fliegen über die See.',
  impact: 'Einschläge am Rumpf — die Entscheidung fällt.',
  aftermath: 'Rauch zieht ab, die Beute wird gezählt...',
  reveal: 'Kampfbericht versiegelt.',
};

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function StatBar({ label, value }) {
  return (
    <div className="pfp-harbormaster-stat">
      <span>{label}</span>
      <div><i style={{ width: `${Math.max(8, Math.min(100, Number(value || 0)))}%` }} /></div>
      <strong>{Number(value || 0)}</strong>
    </div>
  );
}

function buildClientStats(shipLevel, selectedPrep) {
  const hasPrep = (slug) => selectedPrep.includes(slug);
  const attack = Math.min(99, 42 + shipLevel * 7 + (hasPrep('nail_crate') ? 7 : 0) + (hasPrep('iron_fittings') ? 6 : 0));
  const hull = Math.min(99, 46 + shipLevel * 8 + (hasPrep('resin') ? 6 : 0) + (hasPrep('iron_fittings') ? 10 : 0));
  const tempo = Math.min(99, 38 + shipLevel * 7 + (hasPrep('rope') ? 8 : 0) + (hasPrep('sailcloth') ? 5 : 0));
  const fortune = Math.min(99, 34 + shipLevel * 6 + selectedPrep.length * 3);
  return { attack, hull, tempo, fortune, power: Math.round((attack + hull + tempo + fortune) / 4) };
}

function prepCosts(prepItems, selectedPrep) {
  return (prepItems || [])
    .filter((item) => selectedPrep.includes(item.slug))
    .map((item) => ({
      slug: item.slug,
      label: item.label,
      cost: Number(item.cost || 0),
    }))
    .filter((item) => item.slug && item.cost > 0);
}

function prepCostLabel(items) {
  return (items || [])
    .filter((item) => Number(item.cost || 0) > 0)
    .map((item) => `${item.cost}× ${item.label}`)
    .join(' · ');
}

function subtractPrepCosts(items, consumed) {
  const costsBySlug = consumed.reduce((map, item) => {
    map[item.slug] = (map[item.slug] || 0) + Number(item.cost || 0);
    return map;
  }, {});

  return (items || []).map((item) => {
    const consumedAmount = costsBySlug[item.slug] || 0;
    if (!consumedAmount) return item;
    return {
      ...item,
      owned: Math.max(0, Number(item.owned || 0) - consumedAmount),
      selected: false,
      recently_consumed: consumedAmount,
    };
  });
}

export default function HarborMasterPage() {
  const navigate = useNavigate();
  const sessionContext = useOptionalAppSession();
  const { playLoop, stopLoop, playOneShot } = useGameAudio();
  const bootstrapShipyard = sessionContext?.session?.shipyard || null;
  const bootstrapDuels = sessionContext?.session?.harbor_duels || null;
  const mergeSessionPatch = sessionContext?.mergeSessionPatch;
  const [shipyardState, setShipyardState] = useState(() => bootstrapShipyard);
  const [duelState, setDuelState] = useState(() => bootstrapDuels);
  const [loading, setLoading] = useState(!bootstrapShipyard || !bootstrapDuels);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(() => bootstrapDuels?.events?.[0]?.slug || FALLBACK_EVENTS[0].slug);
  const [selectedPrep, setSelectedPrep] = useState(() => (bootstrapDuels?.prep_items || FALLBACK_PREP).filter((item) => item.selected).map((item) => item.slug));
  const [registering, setRegistering] = useState(false);
  const [notice, setNotice] = useState(null);
  const [battlePhase, setBattlePhase] = useState('idle');
  const [lastConsumedPrep, setLastConsumedPrep] = useState([]);
  const battleTimersRef = useRef([]);

  const clearBattleTimers = useCallback(() => {
    battleTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    battleTimersRef.current = [];
  }, []);

  const playCannonSound = useCallback(() => {
    playOneShot('harbor-cannon', cannonFireSoundUrl, { bus: 'effects', volume: 0.58, cooldownMs: 420 });
  }, [playOneShot]);

  const startBattleAudio = useCallback(() => {
    playLoop('harbor-battle', battleSoundUrl, { bus: 'music', volume: 0.42, fadeMs: 180 });
    [800, 1750, 2850, 3950].forEach((delay) => {
      battleTimersRef.current.push(window.setTimeout(playCannonSound, delay));
    });
  }, [playCannonSound, playLoop]);

  const stopBattleAudio = useCallback(() => {
    stopLoop('harbor-battle', 280);
  }, [stopLoop]);

  useEffect(() => () => {
    clearBattleTimers();
    stopBattleAudio();
  }, [clearBattleTimers, stopBattleAudio]);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const [shipyardPayload, duelPayload] = await Promise.all([loadShipyard(), loadHarborDuels()]);
      setShipyardState(shipyardPayload);
      setDuelState(duelPayload);
      const defaultEvent = duelPayload?.events?.[0]?.slug;
      if (defaultEvent) setSelectedEvent((current) => current || defaultEvent);
      setSelectedPrep((duelPayload?.prep_items || []).filter((item) => item.selected).map((item) => item.slug));
      mergeSessionPatch?.({ shipyard: shipyardPayload, harbor_duels: duelPayload, wallet: { wallet: shipyardPayload?.wallet || null } });
    } catch (loadError) {
      setError(loadError);
    } finally {
      setLoading(false);
    }
  }, [mergeSessionPatch]);

  useEffect(() => {
    if (bootstrapShipyard && bootstrapDuels) {
      setShipyardState(bootstrapShipyard);
      setDuelState(bootstrapDuels);
      setLoading(false);
      return;
    }

    refresh();
  }, [bootstrapShipyard, bootstrapDuels, refresh]);

  const ship = shipyardState?.ship || {};
  const shipLevel = Number(ship?.level || 0);
  const unlocked = Boolean(ship?.max_level || shipLevel >= 5);
  const events = duelState?.events?.length ? duelState.events : FALLBACK_EVENTS;
  const prepItems = duelState?.prep_items?.length ? duelState.prep_items : FALLBACK_PREP;
  const opponents = duelState?.recommended_opponents?.length ? duelState.recommended_opponents : FALLBACK_OPPONENTS;
  const selectedEventData = events.find((item) => item.slug === selectedEvent) || events[0];
  const latestMatch = duelState?.latest_match || null;
  const serverStats = duelState?.stats || null;
  const stats = useMemo(() => serverStats || buildClientStats(shipLevel, selectedPrep), [serverStats, shipLevel, selectedPrep]);
  const previewStats = useMemo(() => buildClientStats(shipLevel, selectedPrep), [shipLevel, selectedPrep]);
  const visibleStats = serverStats ? previewStats : stats;
  const shipName = ship?.name || 'Dein Schiff';
  const battleStatus = BATTLE_PHASE_LABELS[battlePhase] || '';
  const battleInProgress = ['preparing', 'broadside', 'impact', 'aftermath'].includes(battlePhase);

  const togglePrep = (slug) => {
    setSelectedPrep((current) => current.includes(slug) ? current.filter((item) => item !== slug) : [...current, slug]);
  };

  const handleRegister = async () => {
    if (!selectedEventData?.slug || registering || battlePhase !== 'idle') return;
    const consumedPreview = prepCosts(prepItems, selectedPrep);
    try {
      setRegistering(true);
      setLastConsumedPrep(consumedPreview);
      if (consumedPreview.length) {
        setDuelState((current) => current ? {
          ...current,
          prep_items: subtractPrepCosts(current.prep_items || prepItems, consumedPreview),
        } : current);
      }
      setNotice({
        type: 'info',
        text: consumedPreview.length
          ? `Der Hafenmeister ruft dein Schiff aus — eingesetzt: ${prepCostLabel(consumedPreview)}.`
          : 'Der Hafenmeister ruft dein Schiff aus — Kanonen laden...',
      });
      clearBattleTimers();
      setBattlePhase('preparing');
      startBattleAudio();
      [
        ['broadside', 700],
        ['impact', 2300],
        ['aftermath', 3900],
      ].forEach(([phase, delay]) => {
        battleTimersRef.current.push(window.setTimeout(() => setBattlePhase(phase), delay));
      });

      const [result] = await Promise.all([
        registerHarborDuel(selectedEventData.slug, selectedPrep),
        sleep(BATTLE_REPLAY_MS),
      ]);

      setBattlePhase('reveal');
      const [freshShipyard, freshDuels] = await Promise.all([loadShipyard(), loadHarborDuels()]);
      setShipyardState(freshShipyard);
      setDuelState(freshDuels);
      setSelectedPrep([]);
      const consumedLabel = result?.match?.prep_consumed_label || (consumedPreview.length ? `Verbraucht: ${prepCostLabel(consumedPreview)}` : '');
      if (result?.session_patch || freshShipyard || freshDuels) {
        mergeSessionPatch?.({
          ...(result?.session_patch || {}),
          shipyard: freshShipyard,
          harbor_duels: freshDuels,
        });
      }
      setNotice({
        type: 'success',
        text: `${result?.message || 'Kampf ausgewertet und Beute gesichert.'}${consumedLabel ? ` ${consumedLabel}.` : ''}`,
      });
      battleTimersRef.current.push(window.setTimeout(() => {
        setBattlePhase('idle');
        stopBattleAudio();
      }, 1800));
    } catch (registerError) {
      setBattlePhase('idle');
      clearBattleTimers();
      stopBattleAudio();
      setLastConsumedPrep([]);
      refresh();
      setNotice({ type: 'error', text: registerError.message });
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return <div className="pfp-harbormaster-page"><div className="pfp-harbormaster-loading">⚓ Der Hafenmeister prüft dein Schiff...</div></div>;
  }

  if (error) {
    return (
      <div className="pfp-harbormaster-page">
        <div className="pfp-harbormaster-loading">
          <h2>Hafenmeister nicht erreichbar</h2>
          <p>{error.message}</p>
          <button type="button" onClick={refresh}>Erneut versuchen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pfp-harbormaster-page">
      <Helmet><title>Hafenmeister – Pfandpirat</title><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="pfp-harbormaster-shell">
        <div className="pfp-harbormaster-topbar">
          <button type="button" onClick={() => navigate('/freibeuterhafen')}>← Zurück zum Hafen</button>
          <button type="button" onClick={() => navigate('/freibeuterhafen/werft')}>⚒️ Zur Werft</button>
        </div>

        <section className="pfp-harbormaster-hero">
          <div>
            <span>Hafenmeister</span>
            <h1>Schiffsduelle & Hafen-Events</h1>
            <p>Der Hafenmeister nimmt Anmeldungen entgegen, ruft Gegner aus, schreibt Kampfberichte und zahlt Beute serverseitig aus.</p>
          </div>
          <aside>
            <small>{unlocked ? 'Freigeschaltet' : 'Noch gesperrt'}</small>
            <strong>{unlocked ? `${shipName} ist kampfbereit` : 'Baue dein Schiff bis Stufe 5 aus'}</strong>
          </aside>
        </section>

        {notice ? <div className={`pfp-harbormaster-notice is-${notice.type}`}>{notice.text}</div> : null}

        <section className={`pfp-harbormaster-board ${unlocked ? 'is-unlocked' : 'is-locked'}`}>
          <nav className="pfp-harbormaster-events" aria-label="Hafen-Events">
            {events.map((item) => (
              <button key={item.slug} type="button" className={item.slug === selectedEventData?.slug ? 'is-active' : ''} onClick={() => setSelectedEvent(item.slug)}>
                <span>{item.badge || item.status || 'Anmeldung offen'}</span>
                <strong>{item.title}</strong>
                <small>{item.rhythm} · {item.fights}</small>
                {item.entries_remaining != null ? <em>{item.entries_remaining} Anmeldung(en) heute frei</em> : null}
              </button>
            ))}
          </nav>

          <article className="pfp-harbormaster-stage">
            <div className="pfp-harbormaster-event-card">
              <div>
                <span>Ausgerufenes Event</span>
                <h2>{selectedEventData?.title}</h2>
                <p>{selectedEventData?.fights}</p>
              </div>
              <aside>
                <small>Belohnung</small>
                <strong>{selectedEventData?.reward_label}</strong>
              </aside>
            </div>

            <div className={`pfp-harbormaster-arena is-battle-${battlePhase}`}>
              <div className="pfp-harbormaster-water" aria-hidden="true" />
              {battleStatus ? <div className="pfp-harbormaster-battle-status">{battleStatus}</div> : null}
              <div className="pfp-harbormaster-projectiles" aria-hidden="true">
                <i className="pfp-harbormaster-cannonball is-player-shot is-one" />
                <i className="pfp-harbormaster-cannonball is-player-shot is-two" />
                <i className="pfp-harbormaster-cannonball is-player-shot is-three" />
                <i className="pfp-harbormaster-cannonball is-opponent-shot is-one" />
                <i className="pfp-harbormaster-cannonball is-opponent-shot is-two" />
              </div>
              <div className="pfp-harbormaster-muzzle is-player" aria-hidden="true" />
              <div className="pfp-harbormaster-muzzle is-opponent" aria-hidden="true" />
              <div className="pfp-harbormaster-impact is-player" aria-hidden="true" />
              <div className="pfp-harbormaster-impact is-opponent" aria-hidden="true" />
              <div className="pfp-harbormaster-ship is-player">
                {ship?.image_url ? <img src={ship.image_url} alt={shipName} /> : <span>⛵</span>}
                <b>{shipName}</b>
              </div>
              <div className="pfp-harbormaster-cannon-flash" aria-hidden="true" />
              <div className="pfp-harbormaster-ship is-opponent">
                {ship?.image_url ? <img src={ship.image_url} alt="" /> : <span>🏴‍☠️</span>}
                <b>{latestMatch?.opponent_name || opponents[0]?.name}</b>
              </div>
            </div>

            <div className="pfp-harbormaster-grid">
              <section>
                <h3>Deine Kampfwerte</h3>
                <div className="pfp-harbormaster-power"><span>Schiffskraft</span><strong>{visibleStats.power}</strong></div>
                <StatBar label="Angriff" value={visibleStats.attack} />
                <StatBar label="Rumpf" value={visibleStats.hull} />
                <StatBar label="Tempo" value={visibleStats.tempo} />
                <StatBar label="Glück" value={visibleStats.fortune} />
              </section>

              <section>
                <h3>Vorbereitung einsetzen</h3>
                <div className="pfp-harbormaster-prep">
                  {prepItems.map((item) => {
                    const active = selectedPrep.includes(item.slug);
                    const missing = Boolean(item.missing || Number(item.owned || 0) < Number(item.cost || 0));
                    return (
                      <button key={item.slug} type="button" className={`${active ? 'is-active' : ''} ${missing ? 'is-missing' : ''}`} onClick={() => togglePrep(item.slug)} disabled={missing}>
                        <strong>{item.label}</strong>
                        <span>Einsatz: {item.cost} Material · Inventar {item.owned || 0}</span>
                        <small>{item.recently_consumed ? `-${item.recently_consumed} verbraucht` : missing ? 'Ressource fehlt' : item.stat}</small>
                      </button>
                    );
                  })}
                </div>
                {lastConsumedPrep.length ? <p className="pfp-harbormaster-consumed">Zuletzt verbraucht: {prepCostLabel(lastConsumedPrep)}</p> : null}
                <p className="pfp-harbormaster-protection">{duelState?.protection_note || 'Ausgewählte Vorbereitungen werden beim Duellstart dauerhaft aus deinem Inventar verbraucht. Keine weiteren Ressourcenverluste durch Niederlage.'}</p>
              </section>
            </div>

            <div className="pfp-harbormaster-opponents">
              {opponents.map((opponent) => (
                <article key={opponent.name}>
                  <span>{opponent.ship}</span>
                  <strong>{opponent.name}</strong>
                  <div><em>Stärke {opponent.power}</em><b>{opponent.odds}</b></div>
                </article>
              ))}
            </div>

            <div className="pfp-harbormaster-report">
              <h3>{battleInProgress ? 'Kampf läuft...' : latestMatch ? `Kampfbericht #${latestMatch.id}: ${latestMatch.result_label}` : 'Letzter Kampfbericht'}</h3>
              {battleInProgress ? (
                <ol className="pfp-harbormaster-live-report">
                  <li className={battlePhase === 'preparing' ? 'is-active' : ''}>Signalflagge gehisst, Gegner nimmt Kurs auf.</li>
                  <li className={battlePhase === 'broadside' ? 'is-active' : ''}>Kanonenfeuer kreuzt die Arena.</li>
                  <li className={battlePhase === 'impact' ? 'is-active' : ''}>Einschläge, Rauch und letzte Manöver.</li>
                  <li className={battlePhase === 'aftermath' ? 'is-active' : ''}>Der Hafenmeister schreibt den Kampfbericht.</li>
                </ol>
              ) : latestMatch ? (
                <ol>
                  {(latestMatch.battle_log || []).map((entry) => (
                    <li key={`${latestMatch.id}-${entry.round}`}><strong>{entry.title}:</strong> {entry.text}</li>
                  ))}
                </ol>
              ) : (
                <ol>
                  <li>Wähle ein Event und Vorbereitungen aus.</li>
                  <li>Der Hafenmeister matcht dein Schiff gegen einen passenden Gegner.</li>
                  <li>Eingesetzte Vorbereitungen werden aus deinem Inventar verbraucht, Loot wird serverseitig ausgezahlt.</li>
                </ol>
              )}
              <div className="pfp-harbormaster-report-action">
                {battleInProgress ? <span>Schlacht läuft — Ergebnis wird gleich enthüllt</span> : latestMatch?.reward_label ? <span>{latestMatch.reward_label}{latestMatch.prep_consumed_label ? ` · ${latestMatch.prep_consumed_label}` : ''}</span> : <span>{selectedEventData?.locked_reason || 'Bereit für Anmeldung'}</span>}
                <button type="button" disabled={!unlocked || !selectedEventData?.can_register || registering || battlePhase !== 'idle'} onClick={handleRegister}>
                  {registering ? 'Kampf läuft...' : unlocked ? 'Schiff anmelden' : 'Erst Schiff voll ausbauen'}
                </button>
              </div>
            </div>
          </article>
        </section>
      </div>
    </div>
  );
}
